'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { ProductPowerSupplyFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPowerSupplyFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { useIntl } from 'react-intl';
import { PowerSupplyPictogramIncluded } from '@packages/shared/src/ui/PowerSupplyPictogram/Dynamic/PowerSupplyPictogramIncluded';
import { PowerSupplyPictogramNotIncluded } from '@packages/shared/src/ui/PowerSupplyPictogram/Dynamic/PowerSupplyPictogramNotIncluded';
import { PowerSupplyPictogramSpecification } from '@packages/shared/src/ui/PowerSupplyPictogram/Dynamic/PowerSupplyPictogramSpecification';

const width = '2.375rem';

/* GraphQL */ `
  fragment ProductPowerSupplyFragment on PowerSupply {
    maxPower
    minPower
    chargerType
    powerSupplyIncluded
  }
`;

export type ProductPowerSupplyProps = {
  maskedData: FragmentType<typeof ProductPowerSupplyFragmentFragmentDoc>;
};

/**
 * Component to display the power supply information of a product, including pictograms and specifications.
 *
 * This component is for use in DV and MiniDV ONLY!!! All other please use the PowerSupplyLocalized component!!!!!
 */

export const ProductPowerSupply = ({ maskedData }: ProductPowerSupplyProps) => {
  const { formatMessage } = useIntl();

  const { maxPower, minPower, chargerType, powerSupplyIncluded } =
    unmask<typeof ProductPowerSupplyFragmentFragmentDoc>(maskedData);

  return (
    <div className="border-color-grey flex flex-row flex-nowrap items-center gap-1.5 rounded-[8px] border p-1.5">
      <div className="flex flex-row gap-1">
        {powerSupplyIncluded === 'included' ? (
          <PowerSupplyPictogramIncluded width={width} />
        ) : (
          <PowerSupplyPictogramNotIncluded width={width} />
        )}
        <PowerSupplyPictogramSpecification
          chargerType={chargerType}
          maxPower={maxPower}
          minPower={minPower}
          width={width}
        />
      </div>
      <div className="text-size-body-sm">
        <div className="font-bold">
          {powerSupplyIncluded === 'included'
            ? formatMessage({ id: 'ProductPowerSupply.Included', defaultMessage: 'Mit Ladegerät' })
            : formatMessage({
                id: 'ProductPowerSupply.NotIncluded',
                defaultMessage: 'Ohne Ladegerät',
              })}
        </div>
        <div>
          {minPower} - {maxPower} W{chargerType === 'PowerDelivery' ? ' USB PD' : ''}
        </div>
      </div>
    </div>
  );
};
